import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { ClockIconComponent } from '../../../app/core/svg-icons/clock-icon.component';

const meta: Meta<ClockIconComponent> = {
  title: 'Atoms/Icons/ClockIcon',
  component: ClockIconComponent,
  parameters: {
    docs: {
      description: {
        component: 'A clock icon component for displaying time-related information. Perfect for delivery times, opening hours, scheduling, and time-sensitive features.',
      },
    },
  },
};

export default meta;
type Story = StoryObj<ClockIconComponent>;

export const Default: Story = {
  render: () => ({
    template: '<app-clock-icon></app-clock-icon>',
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-clock-icon')).toBeTruthy();
  },
};

export const DeliveryInfo: Story = {
  render: () => ({
    template: `
      <div style="display: flex; align-items: center; gap: 1rem; padding: 1rem; border: 1px solid #ddd; border-radius: 8px; background-color: #f8f9fa;">
        <app-clock-icon></app-clock-icon>
        <div>
          <strong>Levering:</strong><br>
          <span style="color: #666;">1-3 arbejdsdage</span>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Clock icon used in delivery information section.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-clock-icon')).toBeTruthy();
  },
};

export const OpeningHours: Story = {
  render: () => ({
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; max-width: 300px;">
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem;">
          <app-clock-icon></app-clock-icon>
          <h4 style="margin: 0;">Åbningstider</h4>
        </div>
        <div style="font-size: 0.9rem; line-height: 1.6;">
          <div style="display: flex; justify-content: space-between;">
            <span>Mandag - Fredag:</span>
            <span>9:00 - 18:00</span>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span>Lørdag:</span>
            <span>10:00 - 16:00</span>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span>Søndag:</span>
            <span>Lukket</span>
          </div>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Clock icon used to display store opening hours information.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-clock-icon')).toBeTruthy();
  },
};

export const EventSchedule: Story = {
  render: () => ({
    template: `
      <div style="background-color: #5C0A31; color: white; padding: 1.5rem; border-radius: 8px; max-width: 400px;">
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem;">
          <app-clock-icon></app-clock-icon>
          <h4 style="margin: 0;">Vinsmagning</h4>
        </div>
        <div style="margin-bottom: 1rem;">
          <strong>Barolo Masterclass</strong><br>
          <span style="opacity: 0.9;">Lør 15. Juni 2024</span>
        </div>
        <div style="display: flex; align-items: center; gap: 0.5rem;">
          <app-clock-icon></app-clock-icon>
          <span>18:00 - 21:00</span>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Clock icon used in event scheduling context with dark wine background.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-clock-icon')).toBeTruthy();
  },
};

export const TimelineEntry: Story = {
  render: () => ({
    template: `
      <div style="display: flex; flex-direction: column; gap: 1rem;">
        <div style="display: flex; align-items: center; gap: 1rem; padding: 1rem; border-left: 3px solid #5C0A31;">
          <app-clock-icon></app-clock-icon>
          <div>
            <div style="font-weight: bold;">Ordre afsendt</div>
            <div style="color: #666; font-size: 0.9rem;">I dag kl. 14:30</div>
          </div>
        </div>
        <div style="display: flex; align-items: center; gap: 1rem; padding: 1rem; border-left: 3px solid #ddd; opacity: 0.6;">
          <app-clock-icon></app-clock-icon>
          <div>
            <div style="font-weight: bold;">Estimeret levering</div>
            <div style="color: #666; font-size: 0.9rem;">I morgen kl. 10:00-16:00</div>
          </div>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Clock icon used in order tracking timeline to show time-based events.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-clock-icon')).toBeTruthy();
  },
};

export const MultipleSizes: Story = {
  render: () => ({
    template: `
      <div style="display: flex; gap: 2rem; align-items: center;">
        <div style="text-align: center;">
          <div>Info Icon</div>
          <div style="transform: scale(0.5); transform-origin: center;">
            <app-clock-icon></app-clock-icon>
          </div>
        </div>
        <div style="text-align: center;">
          <div>Standard</div>
          <app-clock-icon></app-clock-icon>
        </div>
        <div style="text-align: center;">
          <div>Large</div>
          <div style="transform: scale(1.5); transform-origin: center;">
            <app-clock-icon></app-clock-icon>
          </div>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Clock icon shown in different sizes using CSS transforms.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-clock-icon')).toBeTruthy();
  },
};
